import React, { PureComponent, useState } from 'react'
import pure from 'recompose/pure'
import injectSheet from 'react-jss'
import compose from 'recompose/compose'
import { connect } from 'react-redux'
import mapSelectorsToProps from 'aria-viewer/core/selectors/mapSelectorsToProps'

import styleguide from 'aria-viewer/tour/constants/styleGuide'
import colors from 'aria-viewer/tour/constants/style/colors'
import MobileDetect from 'aria-viewer/utils/mobileDetect'

import DesktopShareButton from 'aria-viewer/tour/components/buttons/desktopShareButton'
import DesktopAudioButton from 'aria-viewer/tour/components/buttons/desktopAudioButton'
import StopInfoButton from 'aria-viewer/tour/containers/buttons/StopInfoButton'
import LanguageButton from 'aria-viewer/tour/components/buttons/languageButton'
import InfoButton from 'aria-viewer/tour/components/buttons/infoButton'
import FullscreenButton from 'aria-viewer/tour/components/buttons/fullscreenButton'
import ActionButton from 'aria-viewer/tour/components/buttons/actionButton'
import CloseButton from 'aria-viewer/tour/components/buttons/closeButton'
import AccessibleButton from 'aria-viewer/tour/components/buttons/accessibleButton'
import SearchAIButton from 'aria-viewer/tour/components/buttons/SearchAIButton'
import OverflowButton from '../components/buttons/OverflowButton'
import QueryParamUtils from 'aria-viewer/utils/queryParamUtils'
import { Icon } from 'aria-viewer/common/components/Icon'

import Logo from 'aria-viewer/tour/components/logo'

import SupplementalMediaInlineView from 'aria-viewer/players/supplementalmedia/components/supplementalMediaInlineView'
import { showTourLanguageContextMenu, showShareContextMenu } from 'aria-viewer/contextmenu/contextMenuActions'
import { openRegistrationModal } from 'aria-viewer/registration/registrationActions'
import { CTA_BUTTON_CLICKED } from 'aria-viewer/modal/constants/ModalSources'
import { showOverflowContextMenu } from 'aria-viewer/contextmenu/contextMenuActions'
import { MAIN_HOVER_CLOSE_INDEX, MENU_OVERFLOW_MENU_INDEX } from 'aria-viewer/keyboard/constants/tabIndices'

import { showDescription } from 'aria-viewer/tour/tourActions'
import { setShowSearchAIPrompt } from 'aria-viewer/searchAIPrompt/searchAIPromptActions'
import { infoButtonClicked } from 'aria-viewer/analytics/analyticActions'
import { requestFullScreen, exitFullScreen, isFullScreenActive } from 'aria-viewer/utils/fullScreen'
import { SCREEN_SIZES } from 'aria-viewer/tour/constants'
import BLUR from 'public/assets/svg/hotspots/drop_blur.png'

import { Capabilities, displayFeatureBasedOnViewportSize } from 'aria-viewer/utils/capabilities'
import PrivacyPreferences from 'aria-viewer/tour/components/buttons/privacyPreferencesButton'

export const TOP_RIGHT = 'TOP_RIGHT'
export const RIGHT_TOP = 'RIGHT_TOP'
export const TOP_LEFT = 'TOP_LEFT'
export const BOTTOM_RIGHT = 'BOTTOM_RIGHT'
export const BOTTOM_LEFT = 'BOTTOM_LEFT'

// TODO: Should be using isMobile from DeviceReducer
const isMobile = MobileDetect.mobile() !== null

const styleSheet = theme => ({
    infoButton: {
        '& svg .icon': {
            fill: theme.palette.common.white,
        },
    },
    mobileStopInforButton: {
        filter: 'drop-shadow( 2px 4px 2px rgba(0, 0, 0, .5))',
        width: '48px !important',
        height: '48px !important',
        background: 'none !important',
    },
    fullScreenButton: {
        color: 'white',
        display: 'flex',
        '& span': {
            fontSize: '38px',
        },
    },
    actionButtons: {
        // IWC Styles //

        // First button //
        '& a:first-child': {
            backgroundColor: '#E9075A !important',
        },
        // Second Button //
        '& a:nth-child(2)': {
            backgroundColor: '#FFC837 !important',
        },
        '& a:nth-child(2) span': {
            color: '#000000 !important',
        },
        // Third Button
        '& a:nth-child(3)': {
            backgroundColor: '#2962FF !important',
        },
        '@media (max-width: 730px)': {
            '& a:last-child': {
                display: 'none !important',
            },
        },
        '@media (max-width: 445px)': {
            '& a': {
                display: 'none !important',
            },
            '& a:first-child': {
                display: 'block !important',
            },
        },
    },
})

const style = injectSheet(styleSheet)

const StandardButtonStyle = {
    ...styleguide.buttons.desktop_rounded_button,
    width: '2.5em',
    height: '2.5em',
    background: 'transparent',
    boxShadow: 'none',
}

const StandardButtonStyleWithShadow = {
    filter: 'drop-shadow( 0px 4px 2px rgba(0, 0, 0, .8))',
    ...styleguide.buttons.desktop_rounded_button,
    width: '2.5em',
    height: '2.5em',
    background: 'transparent',
    boxShadow: 'none',
}

const S = {
    containerPosition: {
        topRight: {
            position: 'absolute',
            top: '1.1875em',
            right: 0,
            zIndex: 1999,
        },
        rightTop: {
            position: 'absolute',
            top: '16px',
            right: '0em',
            zIndex: 1999,
        },
        getBottomRight: ({ supplementalMediaPreviewVisible, useResponsiveUI }) => {
            return {
                position: 'absolute',
                bottom: useResponsiveUI ? '0.625em' : '0em',
                right: 9,
                zIndex: supplementalMediaPreviewVisible ? 6 : 4,
                whiteSpace: 'nowrap',
            }
        },
        bottomLeft: {
            display: 'block',
        },
    },
    TopRightOptions: {
        containerPosition: {
            marginRight: '0.625em',
        },
        vrIconContainerStyle: {
            width: '2.5em',
            height: '2.5em',
        },
        accesibleIconContainerStyle: {
            width: '1.875em',
            height: '1.875em',
        },
        close: {
            closeStyle: {
                ...StandardButtonStyle,
                marginLeft: '0.625em',
                width: '2.5em',
                height: '2.5em',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.3)',
            },
            closeSVGStyle: {
                width: '50%',
                left: '25%',
            },
        },
    },
    RightTopOptions: {
        getActionButtonContainer: (isActionButtonsVisible, useResponsiveUI) => {
            return {
                position: 'absolute',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                right: useResponsiveUI ? '140px' : '150px',
                width: '37.1875em',
                transform: `translateX(${isActionButtonsVisible ? 0 : '110%'})`,
                transition: `transform ${styleguide.delay.slow}`,
            }
        },
        getActionButtonsStyle: (isLargeScreen, useResponsiveUI) => {
            return {
                marginBottom: '0.5625em',
                width: ({ useResponsiveUI }) => {
                    if (useResponsiveUI) {
                        return '5.5em'
                    }
                    if (isLargeScreen) {
                        return '8.4375em'
                    } else {
                        return '7.875em'
                    }
                },
                textDecoration: 'none',
                borderRadius: 2,
                padding: useResponsiveUI ? '0.75em 1em' : '0.875em',
                boxShadow: '0 1px 4px 0 rgba(0,0,0,0.3)',
                marginRight: useResponsiveUI ? '0.5em' : '0.625em',
                display: 'inline-block',
            }
        },
        getActionButtonsLabelStyle: isLargeScreen => {
            return {
                cursor: 'pointer',
                fontSize: isMobile ? '16px' : '14px',
                fontWeight: 500,
                textTransForm: 'capitalize',
            }
        },
        close: {
            closeStyle: {
                ...StandardButtonStyle,
                marginLeft: '0.625em',
                width: '2.5em',
                height: '2.5em',
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.3)',
            },
            closeSVGStyle: {
                width: '50%',
                left: '25%',
            },
        },
    },
    BottomRightOptions: {
        mainContainerStyle: {
            minWidth: ({ useResponsiveUI, viewportWidth }) => (useResponsiveUI && viewportWidth < 700 ? null : 200),
        },
        supMediaContainerStyle: {
            position: 'fixed',
            bottom: '25px',
            right: '300px',
        },
        getPictureSizes: isLargeScreen => {
            return {
                width: '98.8px',
                height: '62.6px',
            }
        },
        buttonWrapperIWC: {
            display: 'flex',
            justifyContent: ({ useResponsiveUI }) => (useResponsiveUI ? 'flex-end' : 'space-between'),
            flexDirection: ({ useResponsiveUI }) => (useResponsiveUI ? 'row' : null),
            alignItems: 'center',
            width: '100%',
            position: 'relative',
        },
        buttonWrapper: {
            display: 'block',
            justifyContent: ({ useResponsiveUI }) => (useResponsiveUI ? 'flex-end' : 'space-between'),
            flexDirection: ({ useResponsiveUI }) => (useResponsiveUI ? 'row' : null),
            alignItems: 'center',
            width: '100%',
            position: 'relative',
        },
        buttonsBackground: {
            backgroundImage: `url(${BLUR})`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '50% 50%',
            backgroundSize: '100% 100%',
            width: '100%',
            opacity: 0.3,
            height: '100%',
            position: 'absolute',
            zIndex: -1,
        },
        infoButtonStyle: (descriptionCount, toggleDescriptionVisible) => {
            return {
                ...styleguide.buttons.desktop_rounded_button,
                width: isMobile ? '48px' : '2.5em',
                height: isMobile ? '48px' : '2.5em',
                marginLeft: '0.625em',
                padding: 0,
                opacity: descriptionCount ? 1 : 1,
                background: 'transparent',
                boxShadow: 'none',
            }
        },
    },
    BottomLeftOptions: {
        logoStyle: {
            container: {
                marginTop: 0,
                zIndex: 2001,
                bottom: 15,
                left: 15,
                position: 'absolute',
            },
            poweredBy: {
                color: 'white',
            },
        },
    },
    AccessibleButtonStyle: {
        ...styleguide.buttons.desktop_rounded_button,
        marginLeft: '0.625em',
        marginRight: isMobile ? '0' : 'initial',
        marginTop: '0.4em',
        width: isMobile ? '48px' : '40px',
        height: isMobile ? '48px' : '40px',
        transform: isMobile ?? 'translate(0,-3px)',
        background: 'transparent',
        position: 'relative',
        zIndex: 1,
        boxShadow: 'none',
        ':hover': {
            transform: 'translate(0,-12.5%) scale(1.1)',
            backgroundColor: colors.dark_blue,
        },
        ':active': {
            background: colors.dark_blue_transparent_8,
        },
    },
    AccessibleIconStyle: {
        width: '1.125em',
        height: '1.125em',
        transform: 'translateX(-50%) translateY(-50%)',
        left: '50%',
        top: '50%',
    },
    SearchIconStyle: {
        transform: 'translateX(2%) translateY(8%)',
    },
}

const TopRightOptions = pure(function TopRightOptions({ style, handleCloseClick, isEmbeddedTour }) {
    return (
        <div>
            <div
                style={{
                    marginRight: '0.625em',
                }}
            >
                {isEmbeddedTour && (
                    <CloseButton
                        style={style.close.closeStyle}
                        svgStyle={style.close.closeSVGStyle}
                        puresvg={true}
                        onClick={handleCloseClick}
                        tabIndex={MAIN_HOVER_CLOSE_INDEX}
                    />
                )}
            </div>
        </div>
    )
})

const RightTopOptions = pure(function RightTopOptions({
    style,
    classes,
    isActionButtonsVisible,
    isLargeScreen,
    registrationActionButtons,
    handleActionButtonClick,
    areActionButtonsEnabled,
    useResponsiveUI,
    viewportWidth,
    handleCloseClick,
    isInExpandedState,
    isIWC,
    isRegistrationEnabled,
    actionButtons,
}) {
    const actions = isRegistrationEnabled ? registrationActionButtons : actionButtons.get('actions')
    const isRespAndExpanded = useResponsiveUI && isInExpandedState
    return areActionButtonsEnabled && actions.length > 0 ? (
        <div
            className={isIWC && classes.actionButtons}
            style={style.getActionButtonContainer(isActionButtonsVisible, useResponsiveUI)}
        >
            {actions.map((item, index) => {
                return (
                    <ActionButton
                        onClick={() => {
                            item.actionid == 'register' ? handleActionButtonClick() : null
                        }}
                        style={style.getActionButtonsStyle(isLargeScreen, useResponsiveUI)}
                        labelStyle={style.getActionButtonsLabelStyle(isLargeScreen, useResponsiveUI)}
                        action={item}
                        key={index}
                    >
                        {item.name}
                    </ActionButton>
                )
            })}
            {isRespAndExpanded && (
                <CloseButton
                    style={style.close.closeStyle}
                    svgStyle={style.close.closeSVGStyle}
                    puresvg={true}
                    onClick={handleCloseClick}
                    tabIndex={MAIN_HOVER_CLOSE_INDEX}
                />
            )}
        </div>
    ) : (
        <div className="actionButtons" style={style.getActionButtonContainer(true, useResponsiveUI)}>
            {isRespAndExpanded && (
                <CloseButton
                    style={style.close.closeStyle}
                    svgStyle={style.close.closeSVGStyle}
                    puresvg={true}
                    onClick={handleCloseClick}
                    tabIndex={MAIN_HOVER_CLOSE_INDEX}
                />
            )}
        </div>
    )
})

const RightTopOptionsEnhanced = compose(style, pure)(RightTopOptions)

const BottomRightOptions = ({
    style,
    languageShortName,
    stopIndex,
    toggleDescriptionVisible,
    sizes,
    descriptionCount,
    onSearchButtonClick,
    onInfoButtonClick,
    onLanguageButtonClick,
    onShareClick,
    onOverflowClicked,
    areShareButtonsEnabled,
    classes,
    useResponsiveUI,
    viewportHeight,
    viewportWidth,
    collapseFullScreen,
    expandFullScreen,
    isDeviceMobile,
    isInExpandedState,
    isIWC,
    isSearchAIEnabled,
}) => {
    const isLargeScreen = sizes && sizes.width >= SCREEN_SIZES.size_1350
    const isSmallWidth = sizes && sizes.width <= SCREEN_SIZES.size_945
    const pictureSizes = style.getPictureSizes(isLargeScreen)

    const isMobileAndExpanded = isInExpandedState && isMobile

    const displayDesktopAccessButton =
        isMobileAndExpanded ||
        displayFeatureBasedOnViewportSize(Capabilities.DESKTOP_ACCESS_BUTTON, {
            viewportWidth,
            viewportHeight,
        })

    const displayInfoMenuTourButton = displayFeatureBasedOnViewportSize(Capabilities.INFO_MENU_TOUR_BUTTON, {
        viewportWidth,
        viewportHeight,
    })

    const displayDesktopAudioButton = displayFeatureBasedOnViewportSize(Capabilities.DESKTOP_AUDIO_BUTTON, {
        viewportWidth,
        viewportHeight,
    })

    const displayOverflowButton =
        !isMobile &&
        displayFeatureBasedOnViewportSize(Capabilities.OVERFLOW_BUTTON, {
            viewportWidth,
            viewportHeight,
        })

    return (
        <div style={style.mainContainerStyle}>
            <div style={isIWC || isMobile ? style.buttonWrapperIWC : style.buttonWrapper}>
                <div style={style.buttonsBackground} />
                {isSearchAIEnabled && (
                    <SearchAIButton
                        style={StandardButtonStyle}
                        iconStyle={S.SearchIconStyle}
                        puresvg={true}
                        onClick={onSearchButtonClick}
                    />
                )}
                {displayDesktopAccessButton && (
                    <AccessibleButton
                        style={S.AccessibleButtonStyle}
                        iconStyle={S.AccessibleIconStyle}
                        puresvg={true}
                    />
                )}
                {displayInfoMenuTourButton &&
                    descriptionCount > 0 &&
                    !isMobile &&
                    (!useResponsiveUI ||
                        !displayFeatureBasedOnViewportSize(Capabilities.OVERFLOW_MENU_TOUR_BUTTON, {
                            viewportHeight,
                            viewportWidth,
                        })) && (
                        // Desktop Info button //
                        <InfoButton
                            className={classes.infoButton}
                            style={style.infoButtonStyle(descriptionCount, toggleDescriptionVisible)}
                            icon={'info_desktop'}
                            alt={'toggle description'}
                            puresvg={true}
                            onClick={onInfoButtonClick}
                        />
                    )}
                {//Mobile info button //
                isMobile && <StopInfoButton />}
                {displayDesktopAudioButton && <DesktopAudioButton />}

                {!useResponsiveUI && (
                    <LanguageButton
                        onClick={onLanguageButtonClick}
                        theme="desktop"
                        style={StandardButtonStyle}
                        language={languageShortName}
                    />
                )}
                {!useResponsiveUI && areShareButtonsEnabled && (
                    <DesktopShareButton style={StandardButtonStyle} onClick={onShareClick} offsetY={-185} />
                )}
                {useResponsiveUI && displayOverflowButton && (
                    <OverflowButton
                        style={StandardButtonStyleWithShadow}
                        onClick={onOverflowClicked}
                        tabIndex={MENU_OVERFLOW_MENU_INDEX}
                    />
                )}
                {!useResponsiveUI && (
                    <OverflowButton
                        style={StandardButtonStyleWithShadow}
                        onClick={onOverflowClicked}
                        tabIndex={MENU_OVERFLOW_MENU_INDEX}
                    />
                )}
                {/* The following two buttons 'fullScreenButton' are used to toggle the fullscreen mode in the embedded experience */}
                {useResponsiveUI && (
                    <FullscreenButton
                        className={classes.infoButton}
                        style={style.infoButtonStyle(descriptionCount, toggleDescriptionVisible)}
                        icon={isFullScreenActive() ? 'minimize' : 'expand'}
                        alt={isFullScreenActive() ? 'Collapse fullscreen' : 'Expand fullscreen'}
                        puresvg={true}
                        onClick={isFullScreenActive() ? collapseFullScreen : expandFullScreen}
                    />
                )}
            </div>
        </div>
    )
}

const BottomRightOptionsEnhanced = compose(style, pure)(BottomRightOptions)

const BottomLeftOptions = pure(function BottomLeftOptions({ style }) {
    return (
        <>
            <Logo containerStyle={style.logoStyle.container} poweredByStyle={style.logoStyle.poweredBy} />
            <PrivacyPreferences />
        </>
    )
})

const SupplementalMediaInlinePreview = pure(function SupplementalMediaInlinePreview({
    largeScreen,
    pictureSizes,
    stopIndex,
}) {
    return (
        !MobileDetect.tablet() && (
            <SupplementalMediaInlineView
                width={180}
                height={105}
                index={stopIndex}
                showAmount={3}
                largeScreen={largeScreen}
                pictureSizes={pictureSizes}
            />
        )
    )
})

class TourButtons extends PureComponent {
    constructor(props) {
        super(props)

        this.collapseFullScreen = this.collapseFullScreen.bind(this)
        this.expandFullScreen = this.expandFullScreen.bind(this)
        this.closeWindow = this.closeWindow.bind(this)
    }

    closeWindow = () => {
        parent.postMessage('closeHover', '*')
        return false
    }

    showTourLanguageContextMenu = event => {
        this.props.showTourLanguageContextMenu({
            x: event.x + 40,
            y: event.y - 10,
        })
    }

    showOverflowContextMenu = event => {
        this.props.showOverflowContextMenu({
            x: event.x + 30,
            y: event.y - 20,
        })
    }

    showRegistrationForm = e => {
        this.props.openRegistrationModal(CTA_BUTTON_CLICKED)
    }

    showSearchAIPrompt = () => {
        this.props.setShowSearchAIPrompt(true)
    }

    getCount = () => {
        return Object.keys(this.props.point.descriptions || {}).length
    }

    showDescription = () => {
        this.props.infoButtonClicked()

        if (this.getCount()) {
            this.props.showDescription()
        }
    }

    getContainerPosition = () => {
        const { containerPosition, supplementalMediaPreviewVisible } = this.props

        switch (containerPosition) {
            case TOP_RIGHT:
                return S.containerPosition.topRight
            case RIGHT_TOP:
                return S.containerPosition.rightTop
            case BOTTOM_RIGHT:
                return S.containerPosition.getBottomRight(supplementalMediaPreviewVisible)
            case BOTTOM_LEFT:
                return S.containerPosition.bottomLeft
        }
    }

    collapseFullScreen = () => {
        const { setExpanded } = this.props
        // if (setExpanded) {
        setExpanded && setExpanded(false)
        exitFullScreen && exitFullScreen()
        // }
        //if we are in an iframe tell parent we should dismiss?
        this.closeWindow()
    }

    expandFullScreen = () => {
        const { setExpanded } = this.props
        if (isMobile) {
            this.collapseFullScreen()
            return
        }
        if (setExpanded) {
            setExpanded(true)
            requestFullScreen()
        }
    }

    getButtons = () => {
        const {
            containerPosition,
            language,
            stopIndex,
            toggleDescriptionVisible,
            sizes,
            showShareContextMenu,
            actionButtonsVisible,
            registrationActionButtons,
            areShareButtonsEnabled,
            areActionButtonsEnabled,
            useResponsiveUI,
            viewportHeight,
            viewportWidth,
            isInExpandedState,
            isIWC,
            players,
            isRegistrationEnabled,
            actionButtons,
            isSearchAIEnabled,
        } = this.props

        switch (containerPosition) {
            case TOP_RIGHT:
                return (
                    <TopRightOptions
                        style={S.TopRightOptions}
                        handleCloseClick={this.collapseFullScreen}
                        isEmbeddedTour={window.location.href.indexOf('hover=1') != -1}
                    />
                )
            case RIGHT_TOP:
                return (
                    <RightTopOptionsEnhanced
                        isIWC={isIWC}
                        style={S.RightTopOptions}
                        isActionButtonsVisible={actionButtonsVisible}
                        isLargeScreen={sizes.width >= SCREEN_SIZES.size_1350}
                        registrationActionButtons={registrationActionButtons}
                        isRegistrationEnabled={isRegistrationEnabled}
                        actionButtons={actionButtons}
                        handleActionButtonClick={this.showRegistrationForm}
                        areActionButtonsEnabled={areActionButtonsEnabled}
                        useResponsiveUI={useResponsiveUI}
                        viewportWidth={viewportWidth}
                        handleCloseClick={this.collapseFullScreen}
                        isInExpandedState={isInExpandedState}
                        collapseFullScreen={this.collapseFullScreen}
                        expandFullScreen={this.expandFullScreen}
                    />
                )
            case BOTTOM_RIGHT:
                return (
                    <BottomRightOptionsEnhanced
                        useResponsiveUI={useResponsiveUI}
                        areShareButtonsEnabled={areShareButtonsEnabled}
                        style={S.BottomRightOptions}
                        languageShortName={language ? language.getIn(['current', 'shortname']) : ''}
                        stopIndex={stopIndex}
                        toggleDescriptionVisible={toggleDescriptionVisible}
                        sizes={sizes}
                        descriptionCount={this.getCount()}
                        isSearchAIEnabled={isSearchAIEnabled}
                        onSearchButtonClick={this.showSearchAIPrompt}
                        onInfoButtonClick={this.showDescription}
                        onLanguageButtonClick={this.showTourLanguageContextMenu}
                        onOverflowClicked={this.showOverflowContextMenu}
                        onShareClick={showShareContextMenu}
                        isInExpandedState={isInExpandedState}
                        collapseFullScreen={this.collapseFullScreen}
                        expandFullScreen={this.expandFullScreen}
                        viewportHeight={viewportHeight}
                        viewportWidth={viewportWidth}
                    />
                )
            case BOTTOM_LEFT:
                return <BottomLeftOptions style={S.BottomLeftOptions} useResponsiveUI={useResponsiveUI} />
        }
    }

    render() {
        return <div style={this.getContainerPosition()}>{this.getButtons()}</div>
    }
}

export default compose(
    connect(state => {
        return {
            posMediaPlayers: state.getIn(['interactiveElements', 'positionalMediaPlayers']),
            isIWC: state.getIn(['device', 'isInIframe']) && state.getIn(['device', 'useResponsiveUI']),
            userKey: state.getIn(['analytics', 'userkey']),
            sessionId: state.getIn(['analytics', 'sessionId']),
            viewportWidth: state.getIn(['device', 'viewportWidth']),
            viewportHeight: state.getIn(['device', 'viewportHeight']),
            isDeviceMobile: state.getIn(['device', 'isDeviceMobile']),
            useResponsiveUI: QueryParamUtils.useResponsiveUI(),
        }
    }),
    connect(
        mapSelectorsToProps([
            'originalPlatform',
            'platform',
            'tour',
            'point',
            'stopIndex',
            'language',
            'supplementalMediaPreviewVisible',
            'registrationActionButtons',
            'actionButtonsVisible',
            'isRegistrationEnabled',
            'actionButtons',
            'location',
            'toggleDescriptionVisible',
            'drawer',
            'isWindowFullScreen',
            'areShareButtonsEnabled',
            'areActionButtonsEnabled',
            'isInIframe',
            'isSearchAIEnabled',
        ]),
        {
            showTourLanguageContextMenu,
            showDescription,
            infoButtonClicked,
            showShareContextMenu,
            showOverflowContextMenu,
            openRegistrationModal,
            setShowSearchAIPrompt,
        }
    )
)(TourButtons)
